/**
 * Applications Page
 *
 * US-073: Mobile Card Layout for Applications Page
 *
 * This page displays job applications with responsive layouts:
 * - Mobile (< 640px / sm): Stacked card layout using ApplicationCard component
 *   with a sort dropdown replacing clickable column headers
 * - Tablet (640-1024px / sm to lg): Horizontal scrollable table with overflow-x-auto
 * - Desktop (> 1024px / lg): Full table view with sortable column headers
 *
 * Features:
 * - Search by candidate name
 * - Filter by status, job, and stage
 * - Sort by various columns (desktop headers, mobile dropdown)
 * - Pagination for both views
 * - Reprocess applications
 * - Navigate to application details
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApplications, useReprocessApplication, useJobs, useBatchArchiveApplications, useBatchReprocessApplications, useDownloadResumes } from '../hooks/useApi';
import {
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Eye,
  Search,
  ExternalLink,
  ArrowUpDown,
  CheckSquare,
  Square,
  MinusSquare,
  Archive,
  Download,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ApplicationStatus } from '../types';
import { SearchInput } from '../components/ui/SearchInput';
import { FilterDropdown, type FilterOption } from '../components/ui/FilterDropdown';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { SkeletonTable, SkeletonCardGrid } from '../components/ui/Skeleton';
import { useToast } from '../contexts/ToastContext';
import { ApplicationCard } from '../components/ApplicationCard';

const statusOptions: FilterOption[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'received', label: 'Received' },
  { value: 'enriching', label: 'Enriching' },
  { value: 'enriched', label: 'Enriched' },
  { value: 'ai_processing', label: 'AI Processing' },
  { value: 'ai_complete', label: 'AI Complete' },
  { value: 'synced', label: 'Synced' },
  { value: 'error', label: 'Error' },
];

// Stage filter options
const stageFilterOptions: FilterOption[] = [
  { value: 'active', label: 'Active Candidates' },
  { value: 'all', label: 'All Stages' },
  { value: 'inactive', label: 'Inactive Only' },
  { value: 'NEW', label: 'New' },
  { value: 'IN_REVIEW', label: 'In Review' },
  { value: 'INTERVIEW', label: 'Interview' },
  { value: 'OFFER', label: 'Offer' },
  { value: 'HIRED', label: 'Hired' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'WITHDRAWN', label: 'Withdrawn' },
];

// Inactive stages for visual styling
const INACTIVE_STAGES = ['HIRED', 'REJECTED', 'WITHDRAWN'];

// Sort options for mobile dropdown (replaces clickable column headers)
const sortOptions: FilterOption[] = [
  { value: 'created:desc', label: 'Newest First' },
  { value: 'created:asc', label: 'Oldest First' },
  { value: 'rating:desc', label: 'Highest Rating' },
  { value: 'rating:asc', label: 'Lowest Rating' },
  { value: 'candidate:asc', label: 'Name A-Z' },
  { value: 'candidate:desc', label: 'Name Z-A' },
  { value: 'status:asc', label: 'Status A-Z' },
  { value: 'job:asc', label: 'Job A-Z' },
];

/**
 * Maps application status to Badge variant for consistent color coding across the app
 * Used in both mobile card view and desktop table view
 */
const getStatusBadgeVariant = (status: ApplicationStatus): BadgeVariant => {
  const statusVariantMap: Record<ApplicationStatus, BadgeVariant> = {
    received: 'info',
    enriching: 'warning',
    enriched: 'primary',
    ai_processing: 'primary',
    ai_complete: 'success',
    synced: 'success',
    error: 'danger',
    stub: 'neutral',
    skipped: 'neutral',
  };
  return statusVariantMap[status] || 'default';
};

// US-555: mirrors the API's own MAX_APPLICATIONS cap in
// application-resumes-download.ts — keep these in lockstep.
export const MAX_RESUME_DOWNLOAD = 25;

export default function Applications() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  // Extract filter values from URL
  // US-074: Default to 'ai_complete' when no status param is present
  // Use 'all' as explicit value for "All Statuses" to distinguish from default
  const search = searchParams.get('search') || '';
  const rawStatus = searchParams.get('status');
  const status = rawStatus === null ? 'ai_complete' : rawStatus; // no param = default to ai_complete
  const jobId = searchParams.get('jobId') || '';
  const stages = searchParams.get('stages') || 'active';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 15;

  // US-074: Ensure URL reflects the default status for bookmarkability
  useEffect(() => {
    if (rawStatus === null) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('status', 'ai_complete');
      setSearchParams(newParams, { replace: true });
    }
  }, [rawStatus, searchParams, setSearchParams]);

  // Fetch jobs for the job filter dropdown
  const { data: jobsData } = useJobs({ limit: 100 });

  // Memoize job options to prevent unnecessary re-renders
  const jobOptions: FilterOption[] = useMemo(() => {
    if (!jobsData?.data) return [];
    return jobsData.data.map((job) => ({
      value: String(job.id),
      label: job.title,
    }));
  }, [jobsData?.data]);

  // Update URL params with filter changes, resetting page to 1
  const updateFilters = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      // Reset page to 1 when filters change (unless we're updating page itself)
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Update page in URL
  const setPage = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', String(newPage));
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'created',
    direction: 'desc',
  });

  // Track last refresh timestamp for user feedback (US-059)
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [lastRefreshedText, setLastRefreshedText] = useState<string>('');

  const handleSort = (key: string) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Mobile sort dropdown handler - parses "key:direction" format
  const handleMobileSort = (value: string) => {
    const [key, direction] = value.split(':');
    setSortConfig({ key, direction: direction as 'asc' | 'desc' });
  };

  // Current mobile sort value for the dropdown
  const mobileSortValue = `${sortConfig.key}:${sortConfig.direction}`;

  // US-074: 'all' means no status filter; otherwise pass the specific status
  const effectiveStatus = status === 'all' ? undefined : (status as ApplicationStatus) || undefined;

  const { data, isLoading, error, refetch } = useApplications(
    page,
    limit,
    search || undefined,
    effectiveStatus,
    jobId ? parseInt(jobId, 10) : undefined,
    sortConfig.key,
    sortConfig.direction,
    stages || undefined
  );

  const reprocessMutation = useReprocessApplication();
  const batchArchiveMutation = useBatchArchiveApplications();
  const batchReprocessMutation = useBatchReprocessApplications();
  const downloadResumesMutation = useDownloadResumes();

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const selectionMode = selectedIds.size > 0;

  // Clear selection when page/filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, status, jobId, stages, search]);

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!data?.data) return;
    const allIds = data.data.map(a => a.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  }, [data?.data, selectedIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBatchArchive = async () => {
    const ids = Array.from(selectedIds);
    if (!window.confirm(`Archive ${ids.length} application(s)? This sets their status to "stub".`)) return;
    try {
      await batchArchiveMutation.mutateAsync(ids);
      showToast(`Archived ${ids.length} application(s)`, 'success');
      setSelectedIds(new Set());
    } catch {
      showToast('Failed to archive applications', 'error');
    }
  };

  const handleBatchReprocess = async () => {
    const ids = Array.from(selectedIds);
    if (!window.confirm(`Reprocess ${ids.length} application(s)? This will regenerate AI reviews.`)) return;
    try {
      await batchReprocessMutation.mutateAsync(ids);
      showToast(`Queued ${ids.length} application(s) for reprocessing`, 'success');
      setSelectedIds(new Set());
    } catch {
      showToast('Failed to reprocess applications', 'error');
    }
  };

  // US-555: batch resume download. Server sets the real filename via
  // Content-Disposition; the `download` attribute here is just a fallback.
  const handleDownloadResumes = async () => {
    const ids = Array.from(selectedIds);
    try {
      const blob = await downloadResumesMutation.mutateAsync(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'resumes.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(`Downloaded resumes for ${ids.length} candidate(s)`, 'success');
    } catch (err) {
      showToast((err as Error).message || 'Failed to download resumes', 'error');
    }
  };

  // Update relative time display every minute (US-059)
  useEffect(() => {
    if (!lastRefreshed) return;

    const updateRelativeTime = () => {
      setLastRefreshedText(formatDistanceToNow(lastRefreshed, { addSuffix: true }));
    };

    updateRelativeTime();
    const interval = setInterval(updateRelativeTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lastRefreshed]);

  // Handler for refresh button with toast feedback (US-059)
  const handleRefresh = async () => {
    try {
      await refetch();
      setLastRefreshed(new Date());
      showToast('Applications refreshed successfully', 'success');
    } catch (err) {
      showToast('Failed to refresh applications', 'error');
    }
  };

  // ... rest of logic


  const handleReprocess = async (id: number) => {
    if (
      window.confirm(
        'Are you sure you want to reprocess this application? This will regenerate the AI review.'
      )
    ) {
      try {
        await reprocessMutation.mutateAsync({
          id,
          reason: 'Manual reprocess from admin dashboard',
        });
      } catch (err) {
        console.error('Reprocess failed:', err);
      }
    }
  };

  // Check if any filters are active beyond the defaults (US-074: ai_complete + active are defaults)
  const hasActiveFilters = search || (status && status !== 'ai_complete') || jobId || (stages && stages !== 'active');

  // Clear all filters - resets to defaults (US-074: ai_complete status, active stage)
  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams({ status: 'ai_complete', stages: 'active' }));
  }, [setSearchParams]);

  if (error) {
    return (
      <div className="card p-6">
        <div className="text-center">
          <p className="text-red-600">
            Error loading applications: {error.message}
          </p>
          <button onClick={() => refetch()} className="btn btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Applications</h2>
          <p className="text-sm text-gray-500">
            {data?.pagination.total ?? 0} total applications
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-sm text-gray-500">
              Last refreshed: {lastRefreshedText}
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="card p-4 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-2 sm:gap-4">
          {/* Search Input */}
          <div className="w-full sm:flex-1 sm:min-w-[200px]">
            <SearchInput
              value={search}
              onChange={(value) => updateFilters({ search: value })}
              placeholder="Search by candidate name..."
              label="Search"
              isLoading={isLoading}
            />
          </div>

          {/* Status Filter - US-074: Default to ai_complete, explicit "All Statuses" option */}
          <div className="w-full sm:w-auto">
            <FilterDropdown
              label="Status"
              options={statusOptions}
              value={status}
              onChange={(value) => updateFilters({ status: value })}
              includeAllOption={false}
              size="md"
              fullWidth
              className="sm:w-auto"
            />
          </div>

          {/* Job Filter - max-w-xs on desktop to contain long job titles */}
          <div className="w-full sm:w-auto sm:max-w-xs">
            <FilterDropdown
              label="Job"
              options={jobOptions}
              value={jobId}
              onChange={(value) => updateFilters({ jobId: value })}
              allOptionLabel="All Jobs"
              size="md"
              fullWidth
              className="sm:w-auto"
            />
          </div>

          {/* Stage Filter */}
          <div className="w-full sm:w-auto">
            <FilterDropdown
              label="Stage"
              options={stageFilterOptions}
              value={stages}
              onChange={(value) => updateFilters({ stages: value })}
              size="md"
              fullWidth
              className="sm:w-auto"
            />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="btn btn-secondary text-sm w-full sm:w-auto min-h-[44px]"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Bulk Action Toolbar */}
      {selectionMode && (
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary-700">
              {selectedIds.size} selected
            </span>
            <button
              onClick={handleBatchReprocess}
              disabled={batchReprocessMutation.isPending}
              className="btn btn-secondary text-sm"
            >
              <RotateCcw className={`mr-1.5 h-4 w-4 ${batchReprocessMutation.isPending ? 'animate-spin' : ''}`} />
              Reprocess
            </button>
            <button
              onClick={handleBatchArchive}
              disabled={batchArchiveMutation.isPending}
              className="btn btn-secondary text-sm"
            >
              <Archive className="mr-1.5 h-4 w-4" />
              Archive
            </button>
            <button
              onClick={handleDownloadResumes}
              disabled={selectedIds.size > MAX_RESUME_DOWNLOAD || downloadResumesMutation.isPending}
              className="btn btn-secondary text-sm"
              title={
                selectedIds.size > MAX_RESUME_DOWNLOAD
                  ? `Select ${MAX_RESUME_DOWNLOAD} or fewer to download resumes`
                  : 'Download resumes as a zip'
              }
            >
              <Download className={`mr-1.5 h-4 w-4 ${downloadResumesMutation.isPending ? 'animate-spin' : ''}`} />
              {downloadResumesMutation.isPending ? 'Preparing…' : 'Download resumes'}
            </button>
          </div>
          <button
            onClick={handleClearSelection}
            className="rounded-lg p-2 text-gray-500 hover:bg-primary-100 hover:text-gray-700"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Mobile Card View (< 640px) */}
      <div className="sm:hidden space-y-4">
        {/* Mobile Sort Dropdown */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <FilterDropdown
            label=""
            options={sortOptions}
            value={mobileSortValue}
            onChange={handleMobileSort}
            includeAllOption={false}
            size="md"
            fullWidth
          />
        </div>

        {isLoading ? (
          <SkeletonCardGrid cards={4} columns={2} />
        ) : data?.data.length === 0 ? (
          <div className="card p-12 text-center">
            {status === 'ai_complete' && !hasActiveFilters ? (
              <>
                <FileText className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm font-medium text-gray-700">No reviewed candidates yet</p>
                <p className="mt-1 text-sm text-gray-500">
                  Applications will appear here once AI review is complete.
                </p>
                <button
                  onClick={() => updateFilters({ status: 'all' })}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                >
                  View all applications
                </button>
              </>
            ) : hasActiveFilters ? (
              <>
                <Search className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-gray-500">No applications match your filters</p>
                <button
                  onClick={clearFilters}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all filters
                </button>
              </>
            ) : (
              <>
                <FileText className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-gray-500">No applications found</p>
              </>
            )}
          </div>
        ) : (
          data?.data.map((application) => {
            const isInactive = application.srStage && INACTIVE_STAGES.includes(application.srStage);
            return (
              <ApplicationCard
                key={application.id}
                application={application}
                isInactive={!!isInactive}
                onView={(id) => navigate(`/applications/${id}`)}
                onReprocess={handleReprocess}
                isReprocessing={reprocessMutation.isPending}
                isSelected={selectedIds.has(application.id)}
                onToggleSelect={handleToggleSelect}
                selectionMode={selectionMode}
              />
            );
          })
        )}

        {/* Mobile Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex flex-col gap-3 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500 text-center">
              Showing{' '}
              <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(page * limit, data.pagination.total)}
              </span>{' '}
              of <span className="font-medium">{data.pagination.total}</span>
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="btn btn-secondary flex-1 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
                disabled={page === data.pagination.totalPages}
                className="btn btn-secondary flex-1 disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table View - scrollable on tablet (640-1024px), full width on desktop (>1024px) */}
      <div className="hidden sm:block card overflow-hidden">
        <div className="overflow-x-auto lg:overflow-x-visible">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-3 py-3">
                <button
                  onClick={handleSelectAll}
                  className="rounded p-0.5 hover:bg-gray-200"
                  title={data?.data?.length && data.data.every(a => selectedIds.has(a.id)) ? 'Deselect all' : 'Select all'}
                >
                  {data?.data?.length && data.data.every(a => selectedIds.has(a.id)) ? (
                    <CheckSquare className="h-4 w-4 text-primary-600" />
                  ) : selectedIds.size > 0 ? (
                    <MinusSquare className="h-4 w-4 text-primary-400" />
                  ) : (
                    <Square className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('candidate')}
              >
                <div className="flex items-center gap-1">
                  Candidate
                  {sortConfig.key === 'candidate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('job')}
              >
                <div className="flex items-center gap-1">
                  Job
                  {sortConfig.key === 'job' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Stage
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('rating')}
              >
                <div className="flex items-center gap-1">
                  Rating
                  {sortConfig.key === 'rating' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('created')}
              >
                <div className="flex items-center gap-1">
                  Applied
                  {sortConfig.key === 'created' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Last Activity
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="p-0">
                  <SkeletonTable rows={8} columns={7} showHeader={false} />
                </td>
              </tr>
            ) : data?.data.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center">
                  {status === 'ai_complete' && !hasActiveFilters ? (
                    <>
                      <FileText className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm font-medium text-gray-700">No reviewed candidates yet</p>
                      <p className="mt-1 text-sm text-gray-500">
                        Applications will appear here once AI review is complete.
                      </p>
                      <button
                        onClick={() => updateFilters({ status: 'all' })}
                        className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                      >
                        View all applications
                      </button>
                    </>
                  ) : hasActiveFilters ? (
                    <>
                      <Search className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-gray-500">No applications match your filters</p>
                      <button
                        onClick={clearFilters}
                        className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Clear all filters
                      </button>
                    </>
                  ) : (
                    <>
                      <FileText className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-gray-500">No applications found</p>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              data?.data.map((application) => {
                const isInactive = application.srStage && INACTIVE_STAGES.includes(application.srStage);
                return (
                <tr
                  key={application.id}
                  className={`hover:bg-gray-50 ${isInactive ? 'bg-gray-50 opacity-60' : ''} ${selectedIds.has(application.id) ? 'bg-primary-50' : ''}`}
                >
                  <td className="w-10 px-3 py-4">
                    <button
                      onClick={() => handleToggleSelect(application.id)}
                      className="rounded p-0.5 hover:bg-gray-200"
                    >
                      {selectedIds.has(application.id) ? (
                        <CheckSquare className="h-4 w-4 text-primary-600" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isInactive ? 'bg-gray-200' : 'bg-gray-100'}`}>
                        <FileText className={`h-5 w-5 ${isInactive ? 'text-gray-400' : 'text-gray-600'}`} />
                      </div>
                      <div className="ml-4">
                        <p className={`font-medium ${isInactive ? 'text-gray-500' : 'text-gray-900'}`}>
                          {application.candidateId ? (
                            <Link
                              to={`/candidates/${application.candidateId}`}
                              className={`font-medium ${isInactive ? 'text-gray-500 hover:text-gray-700' : 'text-primary-600 hover:text-primary-700'}`}
                            >
                              {application.candidateFirstName ? `${application.candidateFirstName} ${application.candidateLastName}` : 'Candidate Name Loading...'}
                            </Link>
                          ) : (
                            application.candidateFirstName ? `${application.candidateFirstName} ${application.candidateLastName}` : 'Candidate Name Loading...'
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {application.jobTitle || 'Unknown Job'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-[200px] truncate" title={application.jobTitle}>
                      {application.jobTitle || `#${application.jobId}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <span className={isInactive ? 'text-gray-400 italic' : ''}>
                      {application.srStage || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {application.recommendation ? (
                      <div className="flex items-center gap-1">
                        <span className={`font-medium ${application.recommendation >= 4 ? 'text-green-600' :
                          application.recommendation >= 3 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                          {application.recommendation}/5
                        </span>
                        {application.score && <span className="text-xs text-gray-400">({application.score * 2}%)</span>}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      variant={getStatusBadgeVariant(application.status)}
                      size="sm"
                      rounded
                    >
                      {application.status.replace('_', ' ')}
                    </Badge>
                    {application.errorMessage && (
                      <p className="mt-1 max-w-xs truncate text-xs text-red-500">
                        {application.errorMessage}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDistanceToNow(new Date(application.createdAt), {
                      addSuffix: true,
                    })}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {application.lastActivityAt ? (
                      <span title="Last time a review or update was received from SmartRecruiters">
                        {formatDistanceToNow(new Date(application.lastActivityAt), { addSuffix: true })}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/applications/${application.id}`)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="View details"
                        aria-label="View application details"
                      >
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {application.srApplicationId && (
                        <a
                          href={`https://www.smartrecruiters.com/app/people/applications/${application.srApplicationId}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="View in SmartRecruiters"
                          aria-label="View in SmartRecruiters"
                        >
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        </a>
                      )}
                      <button
                        onClick={() => handleReprocess(application.id)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="Reprocess application"
                        aria-label="Reprocess application"
                        disabled={reprocessMutation.isPending}
                      >
                        <RotateCcw
                          className={`h-4 w-4 ${reprocessMutation.isPending ? 'animate-spin' : ''
                            }`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
            <div className="text-sm text-gray-500">
              Showing{' '}
              <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(page * limit, data.pagination.total)}
              </span>{' '}
              of <span className="font-medium">{data.pagination.total}</span>{' '}
              results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
                disabled={page === data.pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
